import { respondentSchema } from '@algoacademy/shared/mentoring-application/respondent'
import { z } from 'zod'

/**
 * What this browser remembers about a mentoring application: the dialog's typed values, for 7
 * days, until the application is sent, so a visitor who leaves and comes back does not type them
 * twice. A draft from an older dialog keeps its phone and consents; its e-mail, name and goal are
 * dropped. Nothing about a sent application is kept; "Aplikuj na mentoring" always opens the dialog.
 *
 * Every access is guarded: storage can be missing (private mode, blocked cookies) or full, and the
 * dialog must then work exactly as without memory.
 */

/** Minimal surface of the Web Storage API, so callers and tests can supply their own. */
export type BrowserMemory = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const CONTACT_DRAFT_KEY = 'mentoring-application-contact-draft'
/** Held a sent application's id in older versions; only ever removed now. */
const LEGACY_PENDING_APPLICATION_KEY = 'mentoring-application-pending-id'

export const CONTACT_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000

const contactDraftValuesSchema = z.object({
  respondent: respondentSchema,
  phone: z.string().max(30),
  consentContact: z.boolean(),
  consentMarketing: z.boolean()
})

export type ContactDraftValues = z.infer<typeof contactDraftValuesSchema>

const storedContactDraftSchema = z.object({
  savedAt: z.number(),
  values: contactDraftValuesSchema
})

/** The dialog's current values as a storable draft, or null when they do not fit one. */
export function toContactDraftValues(values: unknown): ContactDraftValues | null {
  const parsed = contactDraftValuesSchema.safeParse(values)
  return parsed.success ? parsed.data : null
}

/** The browser's localStorage, or null where it is unavailable. */
export function getBrowserMemory(): BrowserMemory | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

function read(memory: BrowserMemory, key: string): unknown {
  try {
    const raw = memory.getItem(key)
    return raw === null ? null : JSON.parse(raw)
  } catch {
    return null
  }
}

function write(memory: BrowserMemory, key: string, value: unknown): void {
  try {
    memory.setItem(key, JSON.stringify(value))
  } catch {
    // Full or blocked storage: the dialog keeps working, it just does not remember.
  }
}

function remove(memory: BrowserMemory, key: string): void {
  try {
    memory.removeItem(key)
  } catch {
    // Nothing to clean up when storage is blocked.
  }
}

/** The step 1 values typed in the last 7 days, or null. An expired or damaged draft is dropped. */
export function readContactDraft(memory: BrowserMemory, now: number): ContactDraftValues | null {
  const stored = storedContactDraftSchema.safeParse(read(memory, CONTACT_DRAFT_KEY))
  if (stored.success && now - stored.data.savedAt < CONTACT_DRAFT_TTL_MS) return stored.data.values

  remove(memory, CONTACT_DRAFT_KEY)
  return null
}

export function writeContactDraft(
  memory: BrowserMemory,
  values: ContactDraftValues,
  now: number
): void {
  write(memory, CONTACT_DRAFT_KEY, { savedAt: now, values })
}

export function clearContactDraft(memory: BrowserMemory): void {
  remove(memory, CONTACT_DRAFT_KEY)
}

/** Drops an application id an older version of the dialog left in this browser. */
export function removeLegacyPendingApplicationId(memory: BrowserMemory): void {
  remove(memory, LEGACY_PENDING_APPLICATION_KEY)
}
